import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl } from '@material-ui/core';
import {
    ModalActionsComponent,
    PasslySelect,
    PasslyTextField,
    PasslyCheckbox,
    PasslyTab,
    PasslyTabs,
    DirSyncOuTree,
    CopyComponent,
    PasslyButton,
    confirm
} from '@app/components';
import { getLoaderBtn, getModalSwitch, handleGetProvisioningPoliciesDTOData } from '@app/store/actions';
import { IProvisioningPolicyDTO } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import {onPressEnterModal, provisioningStatesSimple} from '@app/helpers';
import SwipeableViews from 'react-swipeable-views';
import { PasslyTabContent } from '@app/componentsV2';
import { syncService } from '@app/services';

export interface IEditAgentData {
    agent: any;
    onCompleteCallback: Function;
}

const AGENT_TYPES: any = { 1: 'AUSI' };

export default function EditAUSIAgentDialog(props: IEditAgentData) {
    const { agent, onCompleteCallback } = props;
    const [syncAUSIAgent, setSyncAUSIAgent] = useState(agent);
    const provisioningPolicies: IProvisioningPolicyDTO[] = useSelector((state: any) => state.provisioningPolicies.provisioningPoliciesDTO);
    const [agentConfiguration, setAgentConfiguration] = useState(JSON.parse(agent.configuration));
    const [provisioningPolicySelected, setProvisioningPolicySelected] = useState(agent.provisioningPolicyId);
    const [agentName, setAgentName] = useState(agent.agentName);
    const [agentNameInvalid, setAgentNameInvalid] = useState(false);
    const [provisioningState, setProvisioningState] = useState(agent.provisioningState);
    const [showSecret, setShowSecret] = useState<boolean>(false);
    const [error, setError] = useState('');

    const dispatch = useDispatch();
    const [tabIndex, setTabIndex] = React.useState(0);
    const { translation } = useTypedTranslation();
    const version = agent.instalations && agent.instalations[0]?.Version;

    useEffect(() => {
        dispatch(handleGetProvisioningPoliciesDTOData());
    }, []);

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'agentName':
                setAgentName(value);
                setAgentNameInvalid(!value);
                break;
            case 'provisioningState':
                setProvisioningState(value);
                break;
            case 'provisioningPolicySelected':
                setProvisioningPolicySelected(value);
                break;
            default:
                break;
        }
    };

    const onCheckboxChange = (checked: boolean) => {
        setAgentConfiguration((previousState: any) => ({ ...previousState, PasswordPolicy: checked }));
    };

    const handlePatchSuccess = () => {
        onCompleteCallback();
    };

    const handlePatchError = (error: any) => {
        dispatch(getLoaderBtn(false));
        const err: string = translation(error?.response?.data[0]?.errorCode === 'already_exist' ? 'INVALID_TAKEN_AGENT_NAME' : 'EDIT_AGENT_ERROR');

        setError(err);
    };

    const fullSync = () => {
        confirm(
            translation('FULL_SYNC_DESCRIPTION'),
            translation('FULL_SYNC_RUN'),
            translation('CANCEL'),
            translation('FULL_SYNC_TITLE'),
            false,
            {}
        ).then(
            () => {
                dispatch(handleFullSync);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const handleFullSync = () => {
        dispatch(getLoaderBtn(true));

        syncService
            .startFullSync(agent.clientId)
            .then(() => dispatch(getLoaderBtn(false)))
            .catch(() => dispatch(getLoaderBtn(false)));
    };

    const patchAgent = () => {
        dispatch(getLoaderBtn(true));
        const agentPatch = {
            agentName: agentName,
            provisioningPolicyId: provisioningPolicySelected,
            configuration: JSON.stringify(agentConfiguration),
            provisioningState: provisioningState
        };
        syncService.patchAUSISyncAgent(agentPatch, agent.id, handlePatchSuccess, handlePatchError);
    };

    const handleSubmitBtn = () => {
        dispatch(getLoaderBtn(true));
        patchAgent();
    };

    const handleClose = () => {
        dispatch(getLoaderBtn(false));
        dispatch(getModalSwitch(false));
    };

    const showSecretKey = () => {
        setShowSecret(true);
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleChangeIndex = (index: number) => {
        setTabIndex(index);
    };

    return (
        <>
            {syncAUSIAgent.installations?.length ? (
                <PasslyTabs value={tabIndex} className='modal__tabs' onChange={handleTabChange}>
                    <PasslyTab label={translation('AGENT_DETAILS')} />
                    <PasslyTab label={translation('AGENT_OUS')} />
                </PasslyTabs>
            ) : null}
            <SwipeableViews index={tabIndex} animateTransitions={false} onChangeIndex={handleChangeIndex}>
                <PasslyTabContent value={tabIndex} index={0}>
                    <form className='modal__form' method='post'>
                        <FormControl>
                            <PasslyTextField
                                fullWidth
                                name='agentName'
                                label={'AGENT_NAME'}
                                value={agentName}
                                onChange={onFormChange}
                                id='agentName-input'
                                inputProps={{ maxLength: 250 }}
                                error={agentNameInvalid}
                                helperText={agentNameInvalid ? translation('REQUIRED_FIELD') : ''}
                                autoFocus />
                        </FormControl>

                        <FormControl>
                            {provisioningState === 1 ? (
                                <PasslySelect
                                    name='provisioningState'
                                    value={provisioningState}
                                    onChange={onFormChange}
                                    id='select-input'
                                    disabled={true}
                                    label={'AGENT_STATE'}
                                    optionValue='id'
                                    options={[{ value: 'Created', label: 'PROVISION_STATE_CREATED', id: 1 }]}
                                />
                            ) : (
                                <PasslySelect
                                    name='provisioningState'
                                    value={provisioningState}
                                    onChange={onFormChange}
                                    id='select-input'
                                    label={'AGENT_STATE'}
                                    optionValue='id'
                                    options={provisioningStatesSimple}
                                />
                            )}
                        </FormControl>

                        <FormControl>
                            <PasslySelect
                                name='provisioningPolicySelected'
                                value={provisioningPolicySelected}
                                onChange={onFormChange}
                                id='selectProvisioning-input'
                                label={'PROVISIONING_POLICY'}
                                optionLabel='DisplayName'
                                optionValue='Id'
                                options={provisioningPolicies}
                                appendTo={document.body}
                            />
                        </FormControl>

                        <FormControl>
                            <h4>{translation('SYNC_PASSWORD_POLICY')}</h4>
                            <PasslyCheckbox
                                name='passwordPolicy'
                                checked={agentConfiguration.PasswordPolicy}
                                onChange={onCheckboxChange}
                                label={translation('ENABLE_PASSWORD_WRITEBACK_CHECK')}
                            />
                        </FormControl>

                        {version && (
                            <div className='dirsync__agent-row'>
                                <div className='dirsync__agent-param-name'>{translation('AGENT_VERSION')}</div>
                                <div>{version}</div>
                            </div>
                        )}
                        <div className='dirsync__agent-row'>
                            <div className='dirsync__agent-param-name'>{translation('AGENT_TYPE')}</div>
                            <div>{AGENT_TYPES[agent.agentType]}</div>
                        </div>
                        <div className='dirsync__agent-row'>
                            <div className='dirsync__agent-param-name'>{translation('AGENT_CLIENT_ID')}</div>
                            <div className='dirsync__agent-text-with-copy'>
                                {agent.clientId}
                                <CopyComponent className='dirsync__agent-copy' valueToCopy={agent.clientId} />
                            </div>
                        </div>
                        <div className='dirsync__agent-row'>
                            <div className='dirsync__agent-param-name'>{translation('AGENT_CLIENT_KEY')}</div>
                            <div className='dirsync__agent-text-with-copy'>
                                {showSecret ? (
                                    <>
                                        {agent.secretKey} <CopyComponent className='dirsync__agent-copy' valueToCopy={agent.secretKey} />
                                    </>
                                ) : (
                                    <span id='showKeySpan' className='detailPage__list--secondary-label link' onClick={showSecretKey}>
                                        {translation('SHOW_KEY')}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className='dirsync__agent-row'>
                            <PasslyButton
                                className='detailPage__button-download'
                                variant='contained'
                                color='primary'
                                href={agent.downloadLink}
                                download
                            >
                                {translation('DOWNLOAD_INSTALLER')}
                            </PasslyButton>
                        </div>

                        {error && (
                            <p>
                                <div className='modal__error'>{error}</div>
                            </p>
                        )}
                    </form>
                </PasslyTabContent>
                {syncAUSIAgent.installations?.length ? (
                    <PasslyTabContent value={tabIndex} index={1}>
                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>{translation('AGENT_OUS')}</div>
                            <div className='detailPage__section-description'>
                                <p>
                                    <div className='ouTree__checkbox ouTree__checkbox-indeterminate' />
                                    <div dangerouslySetInnerHTML={{ __html: translation('OU_TREE_DESCRIPTION_GREEN') }} />
                                </p>
                                <p>
                                    <div className='ouTree__checkbox ouTree__checkbox-part-of-children' />
                                    <div dangerouslySetInnerHTML={{ __html: translation('OU_TREE_DESCRIPTION_LIGHT_BLUE') }} />
                                </p>
                                <p>
                                    <div className='ouTree__checkbox ouTree__checkbox-checked' />
                                    <div dangerouslySetInnerHTML={{ __html: translation('OU_TREE_DESCRIPTION_BLUE') }} />
                                </p>
                            </div>
                            {agentConfiguration.DirectoryStructure ? (
                                <div className='detailPage__section-content'>
                                    <DirSyncOuTree
                                        // syncAUSIAgent={syncAUSIAgent}
                                        agentConfiguration={agentConfiguration}
                                        setSyncAgentConfiguration={setAgentConfiguration}
                                    />
                                </div>
                            ) : null}
                            <PasslyButton id='fullSync' variant='contained' color='primary' onClick={fullSync} className='detailPage__button-sync'>
                                {translation('FULL_SYNC_BUTTON')}
                            </PasslyButton>
                        </div>
                    </PasslyTabContent>
                ) : null}
            </SwipeableViews>

            <ModalActionsComponent
                primaryBtn={translation('SAVE_CHANGES')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleClose}
                disabled={agentNameInvalid || !provisioningPolicySelected}
            />
        </>
    );
}
